import { VoiceToolMeta, VoiceToolSurface, VoiceUITool } from './types'

// A page declares its voice-operable surface as a small manifest of fields,
// choices, and actions. buildVoiceTools compiles that into the VoiceUITool[]
// the assistant registers. This keeps every page's declaration short and
// uniform — and is the single place the token-gate convention is enforced:
// actions tagged `costly` won't fire until the model passes confirmed=true,
// which it is told to do only after the user explicitly asks and confirms.
// See convention-voice-token-gate in project memory.

/**
 * Where a declaration sits and how a model finds it (#1534). Every manifest
 * entry takes these; they reach the generated tool's meta.
 */
export interface VoiceDiscovery {
  /** The part of the page, e.g. `Quick Settings`. */
  section?: string
  /** Extra words ui_search matches. */
  keywords?: string[]
  /** `search` keeps the tool out of the model's direct list. Default `direct`. */
  surface?: VoiceToolSurface
}

/** A text field the user dictates into (e.g. a name or description). */
export interface VoiceTextField extends VoiceDiscovery {
  kind?: 'text'
  /** Full tool name, snake_case, e.g. `set_pipeline_name`. */
  name: string
  /** Human phrase describing what the field is, e.g. `the pipeline name`. */
  label: string
  /** Writes the dictated value through the same setter the input uses. */
  set: (value: string) => void
}

/** A number field (decimal places, a level). The value is clamped to min/max. */
export interface VoiceNumberField extends VoiceDiscovery {
  kind: 'number'
  name: string
  label: string
  min?: number
  max?: number
  set: (value: number) => void
}

export type VoiceField = VoiceTextField | VoiceNumberField

/** An on/off switch (a toggle, a checkbox). */
export interface VoiceToggle extends VoiceDiscovery {
  name: string
  /** What is switched, e.g. `the percent sign`. */
  label: string
  /** Applies the state. May return a short string used as the result detail. */
  set: (on: boolean) => void | string
  enabled?: () => boolean
}

export interface VoiceChoiceOption {
  value: string
  /** Optional gloss shown to the model, e.g. `comments from a TikTok video`. */
  describe?: string
}

/** A selection among a fixed set of options (radio/select/cards). */
export interface VoiceChoice extends VoiceDiscovery {
  name: string
  label: string
  options: Array<string | VoiceChoiceOption>
  /** Applies the choice. May return a short string used as the result detail. */
  select: (value: string) => void | string
  /** When present and false, the choice is rejected with a reason. */
  enabled?: () => boolean
  /**
   * The options come from what is on the page (the folders here, the
   * dashboard's pages), so they are left out of the tool's schema and
   * description, which must stay the same every turn for the page agent's
   * prompt cache (plan D3). Say where the current options are read instead,
   * e.g. `read_current_folder` or `the page description`.
   */
  optionsFrom?: string
}

/** A button-like action (save, run, create, delete). */
export interface VoiceAction extends VoiceDiscovery {
  name: string
  /** Imperative phrase, e.g. `save the pipeline` or `start coding`. */
  label: string
  run: () => void | Promise<unknown>
  /** When present and false, the action is refused with a reason. */
  enabled?: () => boolean
  /**
   * Spends AI tokens / starts a run / costs money. Token-gate: the generated
   * tool refuses to fire unless called with confirmed=true, and its description
   * tells the model to only do so after the user explicitly asks and confirms.
   */
  costly?: boolean
  /**
   * `click`: the user approves it with a click in the pane, never by voice or
   * by the model (#1534). The tool returns needs_confirmation until it is
   * called with confirmed=true, which only the pane's Confirm button passes.
   */
  confirm?: 'click'
  /**
   * With confirm 'click': what the question is about (e.g. the open table's
   * path). The pane lets a question lapse when this changes before Confirm.
   */
  confirmScope?: () => string
  /** Extra clause appended to the description, e.g. what it does/doesn't do. */
  note?: string
}

/** An action performed on a specific item resolved from a collection. */
export interface VoiceItemAction {
  name: string
  /** Imperative phrase that reads with "the X", e.g. `start coding on` → "start coding on the Sky Q pipeline". */
  label: string
  run: (item: unknown) => void | Promise<unknown>
  enabled?: (item: unknown) => boolean
  costly?: boolean
  note?: string
}

/**
 * A list of like entities the user can refer to by name (pipelines, frameworks,
 * dashboards, alerts, apps). Generates a `find_<name>` tool that resolves a
 * spoken name to an id, plus one tool per item action that takes that id. This
 * is how voice drives per-row actions without the model needing to know ids.
 */
export interface VoiceCollection extends VoiceDiscovery {
  /** Singular slug used in tool names, e.g. `pipeline` → `find_pipeline`. */
  name: string
  /** Singular human noun, e.g. `pipeline`. */
  label: string
  /** Current items, read live. */
  list: () => unknown[]
  /** Maps an item to a stable id, a spoken label, and optional extra search text. */
  identify: (item: unknown) => { id: string; label: string; keywords?: string }
  actions: VoiceItemAction[]
}

/**
 * Typed helper for declaring a collection without losing item types at the call
 * site (buildVoiceTools erases to unknown internally).
 */
export function voiceCollection<T>(def: VoiceDiscovery & {
  name: string
  label: string
  list: () => T[]
  identify: (item: T) => { id: string; label: string; keywords?: string }
  actions: Array<{
    name: string
    label: string
    run: (item: T) => void | Promise<unknown>
    enabled?: (item: T) => boolean
    costly?: boolean
    note?: string
  }>
}): VoiceCollection {
  return def as unknown as VoiceCollection
}

/**
 * A read-only view of current page state/data (NOT a capability). Generates a
 * voice tool the realtime model can pull, and — via manifestPageTools — a text
 * assistant PageTool, so both surfaces share situational DATA from one
 * declaration. Only expose data here; capabilities belong in actions/choices so
 * a surface never claims it can do something it has no tool for.
 */
export interface VoiceRead extends VoiceDiscovery {
  name: string
  /** Human phrase, e.g. `the current pipeline state`. */
  label: string
  /** Returns the data as a string (JSON or text). */
  get: () => string | Promise<string>
}

export interface VoicePageManifest {
  fields?: VoiceField[]
  choices?: VoiceChoice[]
  toggles?: VoiceToggle[]
  actions?: VoiceAction[]
  collections?: VoiceCollection[]
  reads?: VoiceRead[]
}

// The discovery fields a declaration set, and only those, so a tool from a
// plain manifest carries the same meta as before.
function discovery(d: VoiceDiscovery): Partial<VoiceToolMeta> {
  return {
    ...(d.section ? { section: d.section } : {}),
    ...(d.keywords?.length ? { keywords: d.keywords } : {}),
    ...(d.surface ? { surface: d.surface } : {}),
  }
}

function readTool(r: VoiceRead): VoiceUITool {
  return {
    name: r.name,
    description: `Read ${r.label}. Returns the current data — call this when the user asks about it, then answer from the result.`,
    meta: { category: 'read', ...discovery(r) },
    parameters: { type: 'object', properties: {}, additionalProperties: false },
    execute: async () => {
      try {
        return await r.get()
      } catch (e) {
        return err(e instanceof Error ? e.message : `failed to read ${r.label}`)
      }
    },
  }
}

/**
 * Adapter: a manifest's `reads` as text-assistant PageTools (zero-arg
 * execute -> string), so the floating text assistant can pull the same page
 * state the voice assistant can. The returned shape is structurally a PageTool
 * (no import needed). Register each read's name in the assistant config's tools
 * + system prompt for the brain to call it.
 */
export function manifestPageTools(manifest: VoicePageManifest): Array<{ name: string; execute: () => Promise<string> }> {
  return (manifest.reads ?? []).map((r) => ({
    name: r.name,
    execute: async () => String(await r.get()),
  }))
}

const ok = (extra?: Record<string, unknown>) => JSON.stringify({ ok: true, ...extra })
const err = (message: string) => JSON.stringify({ error: message })

function optionValue(o: string | VoiceChoiceOption): string {
  return typeof o === 'string' ? o : o.value
}

function describeOptions(options: Array<string | VoiceChoiceOption>): string {
  return options
    .map((o) => (typeof o === 'string' ? o : o.describe ? `${o.value} (${o.describe})` : o.value))
    .join(', ')
}

function fieldTool(f: VoiceField): VoiceUITool {
  if (f.kind === 'number') return numberFieldTool(f)
  return {
    name: f.name,
    description: `Write ${f.label} with text the user dictated. Fills the field only — does not save or submit.`,
    meta: { category: 'field', ...discovery(f) },
    parameters: {
      type: 'object',
      properties: { value: { type: 'string', description: `The text to enter as ${f.label}.` } },
      required: ['value'],
      additionalProperties: false,
    },
    execute: (args) => {
      const value = String(args.value ?? '')
      f.set(value)
      return ok({ set: f.label })
    },
  }
}

function numberFieldTool(f: VoiceNumberField): VoiceUITool {
  const range = f.min !== undefined && f.max !== undefined ? ` (${f.min} to ${f.max})` : ''
  return {
    name: f.name,
    description: `Set ${f.label} to a number${range}. Fills the field only — does not save or submit.`,
    meta: { category: 'field', ...discovery(f) },
    parameters: {
      type: 'object',
      properties: { value: { type: 'number', description: `The number to enter as ${f.label}.` } },
      required: ['value'],
      additionalProperties: false,
    },
    execute: (args) => {
      const n = typeof args.value === 'number' ? args.value : Number(args.value)
      if (!Number.isFinite(n)) return err(`${f.label} must be a number`)
      const value = Math.min(f.max ?? Infinity, Math.max(f.min ?? -Infinity, n))
      f.set(value)
      return ok({ set: f.label, value })
    },
  }
}

function toggleTool(t: VoiceToggle): VoiceUITool {
  return {
    name: t.name,
    description: `Turn ${t.label} on or off.`,
    meta: { category: 'choice', ...discovery(t) },
    parameters: {
      type: 'object',
      properties: { on: { type: 'boolean', description: `true to turn ${t.label} on, false to turn it off.` } },
      required: ['on'],
      additionalProperties: false,
    },
    execute: (args) => {
      if (t.enabled && !t.enabled()) return err(`${t.label} can't be changed right now`)
      if (typeof args.on !== 'boolean') return err('on must be true or false')
      const detail = t.set(args.on)
      return ok(typeof detail === 'string' ? { [t.label]: args.on, detail } : { [t.label]: args.on })
    },
  }
}

function choiceTool(c: VoiceChoice): VoiceUITool {
  const values = c.options.map(optionValue)
  return {
    name: c.name,
    description: c.optionsFrom
      ? `Choose ${c.label}. The options are listed by ${c.optionsFrom}.`
      : `Choose ${c.label}. One of: ${describeOptions(c.options)}.`,
    meta: { category: 'choice', ...discovery(c) },
    parameters: {
      type: 'object',
      properties: {
        value: c.optionsFrom
          ? { type: 'string', description: `The ${c.label} to select.` }
          : { type: 'string', enum: values, description: `The ${c.label} to select.` },
      },
      required: ['value'],
      additionalProperties: false,
    },
    execute: (args) => {
      if (c.enabled && !c.enabled()) return err(`${c.label} can't be changed right now`)
      const value = String(args.value ?? '')
      if (!values.includes(value)) {
        return err(c.optionsFrom ? `unknown ${c.label}: ${value}. The options now: ${describeOptions(c.options) || 'none'}` : `unknown ${c.label}: ${value}`)
      }
      const detail = c.select(value)
      return ok(typeof detail === 'string' ? { selected: value, detail } : { selected: value })
    },
  }
}

function actionTool(a: VoiceAction): VoiceUITool {
  const costlyClause = a.costly
    ? ' This spends AI tokens / starts a run, so only do it when the user explicitly asks for it, tell them it will use tokens, and call again with confirmed=true once they agree. Never chain into it off another action.'
    : ''
  const clickClause = a.confirm === 'click'
    ? ' The user must approve it by clicking Confirm in the pane; you cannot confirm it yourself.'
    : ''
  const description = `${capitalize(a.label)}.${a.note ? ` ${a.note}` : ''}${costlyClause}${clickClause}`
  return {
    name: a.name,
    description,
    meta: {
      category: 'action',
      costly: !!a.costly,
      ...discovery(a),
      // A click-confirmed tool is never in a model's direct list.
      ...(a.confirm === 'click' ? { confirm: 'click' as const, surface: 'search' as const } : {}),
    },
    parameters: {
      type: 'object',
      properties: a.costly
        ? { confirmed: { type: 'boolean', description: 'Set true only after the user has explicitly asked for this and acknowledged it uses tokens.' } }
        : {},
      additionalProperties: false,
    },
    execute: async (args) => {
      if (a.enabled && !a.enabled()) return err(`can't ${a.label} right now`)
      if (a.costly && args.confirmed !== true) {
        return JSON.stringify({
          needs_confirmation: true,
          message: `${capitalize(a.label)} will use AI tokens. Confirm with the user that they want to proceed, then call again with confirmed=true.`,
        })
      }
      if (a.confirm === 'click' && args.confirmed !== true) {
        return JSON.stringify({
          needs_confirmation: true,
          message: `${capitalize(a.label)}? Ask the user to click Confirm in the pane.`,
          args: {},
          ...(a.confirmScope ? { scope: a.confirmScope() } : {}),
        })
      }
      try {
        await a.run()
        return ok({ did: a.label })
      } catch (e) {
        return err(e instanceof Error ? e.message : `failed to ${a.label}`)
      }
    },
  }
}

function capitalize(s: string): string {
  return s.length ? s[0].toUpperCase() + s.slice(1) : s
}

// Rank items against a spoken query: exact label > startsWith > word/substring
// match across label + keywords. Returns the matching {id,label} entries best
// first. An empty query returns everything (so "list the pipelines" works).
function searchCollection(c: VoiceCollection, query: string): Array<{ id: string; label: string }> {
  const q = query.trim().toLowerCase()
  const entries = c.list().map((item) => {
    const { id, label, keywords } = c.identify(item)
    return { id, label, hay: `${label} ${keywords ?? ''}`.toLowerCase() }
  })
  if (!q) return entries.map(({ id, label }) => ({ id, label }))
  const scored = entries
    .map((e) => {
      let score = 0
      if (e.hay === q || e.label.toLowerCase() === q) score = 3
      else if (e.hay.startsWith(q) || e.label.toLowerCase().startsWith(q)) score = 2
      else if (e.hay.includes(q)) score = 1
      return { ...e, score }
    })
    .filter((e) => e.score > 0)
    .sort((a, b) => b.score - a.score)
  return scored.map(({ id, label }) => ({ id, label }))
}

function findTool(c: VoiceCollection): VoiceUITool {
  return {
    name: `find_${c.name}`,
    description: `Find a ${c.label} by name. Returns matching ${c.label}s with their id and label — use the id with the ${c.label} action tools. Call this first when the user names a ${c.label}; if several match, read the labels back and ask which one.`,
    meta: { category: 'collection-find', collection: c.label, ...discovery(c) },
    parameters: {
      type: 'object',
      properties: { query: { type: 'string', description: `Part of the ${c.label}'s name. Empty returns all of them.` } },
      required: ['query'],
      additionalProperties: false,
    },
    execute: (args) => {
      const matches = searchCollection(c, String(args.query ?? '')).slice(0, 6)
      return JSON.stringify({ matches, count: matches.length })
    },
  }
}

function itemActionTool(c: VoiceCollection, a: VoiceItemAction): VoiceUITool {
  const costlyClause = a.costly
    ? ' This spends AI tokens / starts a run, so only do it when the user explicitly asks, tell them it will use tokens, and pass confirmed=true once they agree. Never chain into it.'
    : ''
  return {
    name: a.name,
    description: `${capitalize(a.label)} a ${c.label}, identified by the id from find_${c.name}.${a.note ? ` ${a.note}` : ''}${costlyClause}`,
    meta: { category: 'item-action', collection: c.label, costly: !!a.costly, ...discovery(c) },
    parameters: {
      type: 'object',
      properties: {
        id: { type: 'string', description: `The ${c.label} id (from find_${c.name}).` },
        ...(a.costly ? { confirmed: { type: 'boolean', description: 'Set true only after the user explicitly asked and acknowledged it uses tokens.' } } : {}),
      },
      required: ['id'],
      additionalProperties: false,
    },
    execute: async (args) => {
      const id = String(args.id ?? '')
      const item = c.list().find((it) => c.identify(it).id === id)
      if (!item) return err(`no ${c.label} with id "${id}" — call find_${c.name} first`)
      if (a.enabled && !a.enabled(item)) return err(`can't ${a.label} this ${c.label} right now`)
      if (a.costly && args.confirmed !== true) {
        const { label } = c.identify(item)
        return JSON.stringify({
          needs_confirmation: true,
          message: `${capitalize(a.label)} "${label}" will use AI tokens. Confirm with the user, then call again with confirmed=true.`,
        })
      }
      try {
        await a.run(item)
        return ok({ did: a.label, id })
      } catch (e) {
        return err(e instanceof Error ? e.message : `failed to ${a.label}`)
      }
    },
  }
}

function collectionTools(c: VoiceCollection): VoiceUITool[] {
  return [findTool(c), ...c.actions.map((a) => itemActionTool(c, a))]
}

/**
 * Compile a page's declarative manifest into the VoiceUITool[] the assistant
 * registers. Pure — no React — so it is unit-testable on its own.
 */
export function buildVoiceTools(manifest: VoicePageManifest): VoiceUITool[] {
  return [
    ...(manifest.fields ?? []).map(fieldTool),
    ...(manifest.choices ?? []).map(choiceTool),
    ...(manifest.toggles ?? []).map(toggleTool),
    ...(manifest.actions ?? []).map(actionTool),
    ...(manifest.collections ?? []).flatMap(collectionTools),
    ...(manifest.reads ?? []).map(readTool),
  ]
}
